import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../config';
import { errorMessage } from '../app/errors';
import { log } from './fatalBridge';
import { openDb } from '../data/harnessDb';
import { clearAudioSpike, deleteSegment } from '../data/audioSpikeStore';
import { buildAudioReport } from '../harness/audioScenario';
import {
  assembleForPlayback,
  beginSegment,
  buildAndSaveRun,
  completeCapabilities,
  createSpikeContext,
  loadSegmentRecords,
  probeCapabilities,
  recoverIncompleteSegments,
  setManualVerdict,
} from '../harness/audioSession';
import type { ActiveSegment } from '../harness/audioSession';
import { measureAcquisition } from '../harness/audioRecorder';
import { loadAudioRuns } from '../harness/audioResultsStore';
import {
  buildAudioBundle,
  copyAudioBundleText,
  downloadAudioBundle,
  shareAudioBundle,
} from '../harness/exportAudioResults';
import { captureEnvironment } from '../harness/environment';
import { probeStorage } from '../harness/storageProbe';
import { AudioReportHeader, AudioResultsView } from './AudioResultsView';
import type {
  AssemblyVariant,
  AudioCapabilityReport,
  AudioRunRecord,
  GumTrial,
  ManualVerdict,
  SegmentRecord,
  StorageProbe,
} from '../types';

/**
 * Build unit U-1 — the `MediaRecorder` spike, on one screen.
 *
 * **This screen cannot answer its own question anywhere but on Andrew's iPhone.** Chromium's
 * `MediaRecorder` produces WebM/Opus and iOS produces fragmented MP4/AAC, and A-2 is a claim about
 * the second. Every verdict here therefore runs through the ENV-1 gate in `harness/scenario.ts` and
 * reads `inconclusive` off the target device — a desktop run is calibration, and the banner at the
 * top says so before it says anything else.
 *
 * The screen is built for a venue: no navigation, no modals, large targets, every failure on screen
 * in words. Three things it does that a desk cannot: it survives being backgrounded mid-recording,
 * it recovers segments a hard kill left behind, and it lets Andrew press Play and say what he
 * actually heard.
 */

type Phase = 'boot' | 'idle' | 'acquiring' | 'recording' | 'processing' | 'measuringGum';

interface Draft {
  capabilities: AudioCapabilityReport | null;
  gumTrials: GumTrial[];
  /**
   * Probed on mount rather than only at export. Rendering an all-null storage block during a
   * session reads as "the storage API is unsupported", which is a different and much more alarming
   * fact than "we have not asked yet" — and BAK-3 is exactly the kind of thing that gets glanced at
   * once, at a venue, and believed.
   */
  storage: StorageProbe | null;
  failures: { stage: string; message: string }[];
}

const UNPROBED_STORAGE: StorageProbe = {
  supported: false,
  usageBytes: null,
  quotaBytes: null,
  usageFraction: null,
  persistedBefore: null,
  persistRequested: null,
  persistedAfter: null,
  breakdown: null,
  error: 'not probed yet',
};

export function AudioHarness() {
  const ctx = useMemo(() => createSpikeContext(), []);

  const [phase, setPhase] = useState<Phase>('boot');
  const [status, setStatus] = useState('Probing audio capabilities…');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft>({
    capabilities: null,
    gumTrials: [],
    storage: null,
    failures: [],
  });
  const [segments, setSegments] = useState<SegmentRecord[]>([]);
  const [history, setHistory] = useState<AudioRunRecord[]>([]);

  const [label, setLabel] = useState('plain 12s');
  const [seconds, setSeconds] = useState(config.AUDIO_SPIKE_SEGMENT_S);
  const [matchMinute, setMatchMinute] = useState(config.AUDIO_SPIKE_DEFAULT_MATCH_MINUTE);
  const [requestedMime, setRequestedMime] = useState('');
  const [gumTrialCount, setGumTrialCount] = useState(config.GUM_TRIALS_DEFAULT);
  const [liveChunks, setLiveChunks] = useState(0);

  const activeRef = useRef<ActiveSegment | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playUrlRef = useRef<string | null>(null);

  /**
   * The report is rebuilt from live state on every render rather than read back from a stored run,
   * so what is on screen is always the current session — including the segment recorded thirty
   * seconds ago that has not been exported yet.
   */
  const liveRun = useMemo<AudioRunRecord>(
    () => ({
      id: 'live',
      startedAt: segments[segments.length - 1]?.startedAt ?? Date.now(),
      finishedAt: Date.now(),
      capabilities: draft.capabilities ?? {
        mediaRecorderSupported: false,
        getUserMediaSupported: false,
        permissionState: 'unsupported',
        supportMatrix: [],
        defaultMime: null,
        probedAt: Date.now(),
      },
      gumTrials: draft.gumTrials,
      segments,
      environment: captureEnvironment(),
      storage: draft.storage ?? UNPROBED_STORAGE,
      failures: draft.failures,
      notes: null,
    }),
    [draft, segments],
  );

  const report = useMemo(() => buildAudioReport(liveRun, Date.now()), [liveRun]);

  const refreshSegments = useCallback(async () => {
    setSegments(await loadSegmentRecords());
  }, []);

  const guard = useCallback(async (stage: string, work: () => Promise<void>) => {
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (cause) {
      log.exception('audio-ui', `${stage} failed`, cause);
      setError(`${stage} failed — ${errorMessage(cause)}`);
      setDraft((d) => ({
        ...d,
        failures: [...d.failures, { stage, message: errorMessage(cause) }],
      }));
      setPhase('idle');
    }
  }, []);

  // Boot: capabilities first (no microphone needed), then design §8.5's recovery pass. The recovery
  // pass runs on entering this screen because that is the app's `pageshow` for the audio subsystem —
  // it is what turns "the chunks survived a hard kill" from a claim into a result on screen.
  useEffect(() => {
    void (async () => {
      try {
        await openDb(() => performance.now());
        const caps = await probeCapabilities(ctx.platform);
        // `requestPersist: false` — BAK-3's persist() request belongs to the export step, so the
        // screen reports the grant the device already had rather than one this screen just asked for.
        const storage = await probeStorage(false);
        setDraft((d) => ({ ...d, capabilities: caps, storage }));

        setStatus('Checking for segments left by a previous launch…');
        const recovered = await recoverIncompleteSegments(ctx);
        if (recovered.length > 0) {
          setNotice(
            `Recovered ${recovered.length} segment(s) that a hard kill left unfinalized. Design §8.5 predicts at most one ${config.TIMESLICE_MS} ms timeslice lost — check below whether they still play.`,
          );
        }

        await refreshSegments();
        setHistory(await loadAudioRuns());
        setStatus('Ready');
        setPhase('idle');
      } catch (cause) {
        setError(errorMessage(cause));
        setStatus('Could not start the audio harness');
        setPhase('idle');
      }
    })();

    return () => {
      // NFR-5 / §8.1: never hold the stream outside the surface that needs it. Leaving the screen
      // with the iOS recording indicator lit would be both a privacy surprise and a battery cost.
      ctx.mic.release();
      if (playUrlRef.current) URL.revokeObjectURL(playUrlRef.current);
    };
  }, [ctx, refreshSegments]);

  // ── Microphone ─────────────────────────────────────────────────────────────

  const doAllowMic = () =>
    guard('Microphone', async () => {
      setPhase('acquiring');
      setStatus('Requesting the microphone…');
      await ctx.mic.acquire();
      // `defaultMime` needs a live stream, so the capability report is only complete now. AUD-1
      // stores what the recorder emits, and this is the first moment the browser will say what
      // that is.
      const caps = draft.capabilities;
      if (caps) {
        const filled = await completeCapabilities(ctx, caps);
        setDraft((d) => ({ ...d, capabilities: filled }));
      }
      setStatus(
        'Microphone held — the iOS recording indicator is lit, which is honest: the app can record at any instant (§8.1).',
      );
      setPhase('idle');
    });

  const doReleaseMic = () =>
    guard('Microphone', async () => {
      ctx.mic.release();
      setStatus('Microphone released.');
    });

  const doMeasureAcquisition = (releaseBetween: boolean) =>
    guard(releaseBetween ? 'Cold acquisition' : 'Warm acquisition', async () => {
      setPhase('measuringGum');
      if (!releaseBetween) {
        // Pre-acquire so every trial in the warm pass is genuinely warm; otherwise the first is a
        // cold acquisition wearing a warm label.
        await ctx.mic.acquire();
      }
      const trials = await measureAcquisition({
        trials: gumTrialCount,
        mic: ctx.mic,
        platform: ctx.platform,
        releaseBetween,
        onProgress: (done, total) => setStatus(`Acquisition ${done}/${total}`),
      });
      setDraft((d) => ({ ...d, gumTrials: [...d.gumTrials, ...trials] }));
      setStatus('Acquisition pass complete');
      setPhase('idle');
    });

  // ── Recording ──────────────────────────────────────────────────────────────

  const startRecording = (plannedSeconds: number, segmentLabel: string) =>
    guard('Recording', async () => {
      setPhase('acquiring');
      setLiveChunks(0);
      const active = await beginSegment(ctx, {
        label: segmentLabel,
        matchMinuteAtStart: matchMinute,
        plannedSeconds,
        requestedMime: requestedMime === '' ? null : requestedMime,
        verifyRecorderReuse: true,
        onChunk: (count) => setLiveChunks(count),
        onPhase: (p) => setStatus(p),
      });
      activeRef.current = active;
      setPhase('recording');

      // Awaited here, not in the click handler, so the screen stays live for the whole recording —
      // including while the app is backgrounded, which is the case AUD-3 is about.
      const record = await active.done;
      activeRef.current = null;
      await refreshSegments();
      setPhase('idle');
      setStatus(
        `Segment finished (${record.trigger}) — ${record.chunks.length} chunk(s), ${record.totalBytes} bytes, mime ${record.actualMime ?? 'none reported'}`,
      );
    });

  const stopRecording = () => {
    activeRef.current?.stop('userStop');
  };

  // ── Playback: the ear, which overrules the probe ───────────────────────────

  const doPlay = (segmentId: string, variant: AssemblyVariant) =>
    void guard('Playback', async () => {
      const assembled = await assembleForPlayback(segmentId, variant);
      if (!assembled) throw new Error('That assembly could not be built from the stored chunks.');

      if (playUrlRef.current) URL.revokeObjectURL(playUrlRef.current);
      const url = URL.createObjectURL(assembled.blob);
      playUrlRef.current = url;

      const el = audioRef.current;
      if (!el) throw new Error('The audio element is not mounted.');
      el.src = url;
      // Called directly inside the tap's user activation — iOS refuses playback started from a
      // later task, and a refusal here would read as an unplayable file.
      await el.play();
      setNotice(`Playing ${variant}. Say what you heard on the segment card.`);
    });

  const doVerdict = (segmentId: string, verdict: ManualVerdict) =>
    void guard('Verdict', async () => {
      await setManualVerdict(segmentId, verdict);
      await refreshSegments();
    });

  const doDeleteSegment = (segmentId: string) =>
    void guard('Delete segment', async () => {
      await deleteSegment(segmentId);
      await refreshSegments();
    });

  // ── Getting it off the phone ───────────────────────────────────────────────

  const saveAndBundle = useCallback(async () => {
    const caps = draft.capabilities;
    if (!caps) throw new Error('Capabilities have not been probed yet.');
    const saved = await buildAndSaveRun({
      capabilities: caps,
      gumTrials: draft.gumTrials,
      segments,
      failures: draft.failures,
      requestPersist: true,
    });
    setHistory(await loadAudioRuns());
    return buildAudioBundle(saved, history, Date.now());
  }, [draft, segments, history]);

  const doShare = () =>
    guard('Export', async () => {
      const route = await shareAudioBundle(await saveAndBundle());
      setNotice(route === 'download' ? 'Downloaded.' : 'Shared.');
    });

  const doCopy = () =>
    guard('Copy', async () => {
      await copyAudioBundleText(await saveAndBundle());
      setNotice('Summary copied to the clipboard.');
    });

  const doDownload = () =>
    guard('Download', async () => {
      downloadAudioBundle(await saveAndBundle());
      setNotice('Downloaded.');
    });

  const doClearAudio = () =>
    guard('Clear audio', async () => {
      await clearAudioSpike();
      await refreshSegments();
      setStatus('All spike audio deleted from the phone.');
    });

  const busy = phase === 'acquiring' || phase === 'processing' || phase === 'measuringGum';
  const recording = phase === 'recording';

  return (
    <>
      <h1>Touchline U-1 — MediaRecorder spike</h1>
      <p className="sub">
        Settles assumption <strong>A-2</strong>: does iOS Safari emit a timesliced chunk sequence
        that, concatenated verbatim, plays back — and does it still play when the last chunk is
        missing? Also measures <code>getUserMedia</code> acquisition cost (FLAG-1) and what MIME type
        this browser actually emits (AUD-1). Only a run on the target iPhone, from the home-screen
        icon, settles any of it.
      </p>

      {error && <div className="error">{error}</div>}

      {/* Above the controls, deliberately: relaunching at a venue must show "is this evidence" and
          "does A-2 hold" without a scroll. */}
      <AudioReportHeader report={report} />

      {notice && (
        <div className="card">
          <strong>{notice}</strong>
        </div>
      )}

      {recording && (
        <div className="card recording">
          <strong>RECORDING — {liveChunks} chunk(s)</strong>
          <p className="note">
            To test AUD-3 you have to interrupt this for real. Switch apps, lock the phone, or — the
            one that matters — <strong>take an actual phone call</strong>. Every one of those routes
            through the same finalize, and the chunks already written are already on disk.
          </p>
          <button className="danger" onClick={stopRecording}>
            Stop
          </button>
        </div>
      )}

      {busy && (
        <div className="card">
          <strong>{status}</strong>
        </div>
      )}

      {!recording && !busy && (
        <>
          <div className="card">
            <h2>1 · Microphone</h2>
            <p className="sub">
              Touchline records voice notes and flags while you watch. Audio stays on your phone and
              in your own backup — nothing is uploaded to anyone else, and nothing is transcribed.
              (AUD-4’s plain-language purpose, as design §8.1 words it.)
            </p>
            <div className="row two">
              <button className="primary" onClick={() => void doAllowMic()}>
                Allow microphone
              </button>
              <button onClick={() => void doReleaseMic()}>Release</button>
            </div>
            <label className="field">
              Acquisition trials ({config.GUM_TRIALS_MIN}–{config.GUM_TRIALS_MAX})
              <input
                type="number"
                inputMode="numeric"
                min={config.GUM_TRIALS_MIN}
                max={config.GUM_TRIALS_MAX}
                value={gumTrialCount}
                onChange={(e) => setGumTrialCount(Number(e.target.value) || config.GUM_TRIALS_DEFAULT)}
              />
            </label>
            <div className="row two">
              <button onClick={() => void doMeasureAcquisition(true)}>Measure cold</button>
              <button onClick={() => void doMeasureAcquisition(false)}>Measure warm</button>
            </div>
            <p className="note">
              Cold releases the stream between trials, so each pays a real <code>getUserMedia</code>.
              Warm keeps it held and measures tap → recording, which is what FLAG-1 actually costs.
            </p>
          </div>

          <div className="card">
            <h2>2 · Record a segment</h2>
            <label className="field">
              Label
              <input value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <label className="field">
              Length (seconds, capped at VOICE_NOTE_MAX_S = {config.VOICE_NOTE_MAX_S})
              <input
                type="number"
                inputMode="numeric"
                min={config.AUDIO_SPIKE_SEGMENT_MIN_S}
                max={config.VOICE_NOTE_MAX_S}
                value={seconds}
                onChange={(e) => setSeconds(Number(e.target.value) || config.AUDIO_SPIKE_SEGMENT_S)}
              />
            </label>
            <label className="field">
              Pretend match minute (SES-10 stamps at start; {config.AUDIO_SPIKE_DEFAULT_MATCH_MINUTE}′
              is the boundary case §8.2 names)
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={config.AUDIO_SPIKE_MAX_MATCH_MINUTE}
                value={matchMinute}
                onChange={(e) => setMatchMinute(Math.max(0, Number(e.target.value) || 0))}
              />
            </label>
            <label className="field">
              Force a MIME type — leave empty to let the browser choose, which is the product path
              <select value={requestedMime} onChange={(e) => setRequestedMime(e.target.value)}>
                <option value="">(browser default)</option>
                {(draft.capabilities?.supportMatrix ?? [])
                  .filter((m) => m.supported)
                  .map((m) => (
                    <option key={m.mime} value={m.mime}>
                      {m.mime}
                    </option>
                  ))}
              </select>
            </label>
            <button className="primary" onClick={() => void startRecording(seconds, label)}>
              Record {seconds}s
            </button>
            <button onClick={() => void startRecording(config.VOICE_NOTE_MAX_S, `${label} — interrupt me`)}>
              Record until I stop (or something interrupts it)
            </button>
            <p className="note">
              Record at least {config.AUDIO_SPIKE_MIN_SEGMENTS} segments of{' '}
              {config.AUDIO_SPIKE_SEGMENT_S}s before the verdict will read anything but{' '}
              <em>inconclusive</em> — one playable assembly is an anecdote about one recording.
            </p>
          </div>
        </>
      )}

      {/* Mounted always, so `play()` can be called synchronously inside a tap handler. iOS refuses
          playback initiated from a later task. */}
      <audio ref={audioRef} controls className="playback" />

      <AudioResultsView
        report={report}
        run={liveRun}
        onPlay={doPlay}
        onVerdict={doVerdict}
        onDelete={doDeleteSegment}
        busy={busy || recording}
      />

      {!recording && !busy && (
        <div className="card">
          <h2>3 · Get it off the phone</h2>
          <div className="row two">
            <button className="primary" onClick={() => void doShare()}>
              Share
            </button>
            <button onClick={() => void doCopy()}>Copy text</button>
          </div>
          <button onClick={() => void doDownload()}>Download JSON</button>
          <p className="note">
            Exporting saves the run first, so the session survives the app being closed.{' '}
            {history.length} previous run(s) stored. The audio itself is not exported — it is
            megabytes, and the question is whether it played, not what it said.
          </p>
          <button className="danger" onClick={() => void doClearAudio()}>
            Delete all spike audio
          </button>
          <p className="note">Status: {status}</p>
        </div>
      )}
    </>
  );
}
